<script setup>
import {onUpdated, ref} from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import {useLanguageStore} from "@/pinia";
import {useToast} from "@/composables/useToast";

const toast = useToast();
const languageStore = useLanguageStore();

const ageId= ref("");
const ageTitle = ref("");
const startDt = ref("");
const endDt = ref("");
const ageOrder = ref("");
const regUser = ref("");
const regDt = ref("");
const isModify = ref(false);

const props = defineProps({
  isAgeDetailModal : Boolean,
  age : {}
})

const emits = defineEmits([
  'set_isAgeDetailModal',
  'fn_ModifyAge'
])

const set_isModify = (is)=>{
  isModify.value = is
}
const fn_ModifyAge = ()=>{
  if(!ageTitle.value || ageTitle.value.trim() === '') {
    toast.warning(languageStore.get('fillInBlank'));
    return;
  }
  if(!startDt.value) {
    toast.warning(languageStore.get('fillInBlank'));
    return;
  }
  if(!endDt.value) {
    toast.warning(languageStore.get('fillInBlank'));
    return;
  }
  let data = {
    "ageId": ageId.value,
    "ageTitle": ageTitle.value,
    "startDt": startDt.value,
    "endDt": endDt.value,
    "ageOrder": ageOrder.value,
    "regUser": regUser.value,
    "regDt": regDt.value
  }
  emits('fn_ModifyAge', data);
  close();
}
const close = ()=>{
  isModify.value = false;
  emits('set_isAgeDetailModal', false);
}

onUpdated(()=>{
  let a = props.age;
  ageId.value = a.ageId
  ageTitle.value = a.ageTitle;
  startDt.value =a.startDt;
  endDt.value = a.endDt;
  ageOrder.value = a.ageOrder;
  regUser.value = a.regUser;
  regDt.value = a.regDt;
})
</script>

<template>
  <v-dialog
      :model-value="isAgeDetailModal"
      @update:model-value="close"
      width="auto"
  >
    <v-card
        :title="languageStore.get('age')"
        width ="600"
    >
      <v-card-text>
        <v-text-field
            v-model="ageId"
            :label="languageStore.get('sessionId')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            readonly
            :disabled="isModify"
            class="mb-4"
        ></v-text-field>

        <v-text-field
            v-model="ageTitle"
            :label="languageStore.get('sessionName')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!isModify"
            class="mb-4"
        ></v-text-field>

        <v-row>
          <v-col cols="6">
            <vue-date-picker
                class="dp-full-width"
                locale="ko"
                v-model="startDt"
                :enable-time-picker="false"
                :placeholder="languageStore.get('selectStartDate')"
                :readonly="!isModify"
                :teleport="true"
            />
          </v-col>
          <v-col cols="6">
            <vue-date-picker
                class="dp-full-width"
                locale="ko"
                v-model="endDt"
                :enable-time-picker="false"
                :placeholder="languageStore.get('selectEndDate')"
                :readonly="!isModify"
                :teleport="true"
            />
          </v-col>
        </v-row>

        <v-text-field
            v-model="ageOrder"
            :label="languageStore.get('order')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!isModify"
            class="mb-4 mt-4"
        ></v-text-field>

        <v-text-field
            v-model="regDt"
            :label="languageStore.get('createdDate')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            readonly
            :disabled="isModify"
            class="mb-4"
        ></v-text-field>

        <v-text-field
            v-model="regUser"
            :label="languageStore.get('createdBy')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            readonly
            :disabled="isModify"
        ></v-text-field>
      </v-card-text>
      <v-card-actions class="modal-actions">
        <v-spacer></v-spacer>
        <template v-if="isModify">
          <v-btn
              color="primary"
              variant="flat"
              @click="fn_ModifyAge"
          >{{ languageStore.get('save') }}</v-btn>
        </template>
        <template v-else>
          <v-btn
              color="primary"
              variant="flat"
              @click="set_isModify(true)"
          >{{ languageStore.get('edit') }}</v-btn>
        </template>
        <v-btn
            variant="outlined"
            @click="close"
        >{{ languageStore.get('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 스타일 */
:deep(.v-card-item) {
  background-color: #1A3A5C !important;
  padding: 0 !important;
}

:deep(.v-card-title) {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px !important;
  font-weight: 600 !important;
  font-size: 18px !important;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text) {
  padding: 24px !important;
}

/* v-card-text 내 입력 필드 간격 */
.v-card-text :deep(.v-input),
.v-card-text :deep(.dp-full-width) {
  margin-bottom: 16px;
}

/* v-row 내부의 v-input은 margin 제외 */
.v-card-text :deep(.v-row .v-input),
.v-card-text :deep(.v-row .dp-full-width) {
  margin-bottom: 0;
}

.v-card-text :deep(.v-row) {
  margin-top: 0;
  margin-bottom: 16px;
}

.v-card-text :deep(.v-col) {
  padding-top: 0;
  padding-bottom: 0;
}

.v-card-text :deep(.v-input:last-child) {
  margin-bottom: 0;
}

/* DatePicker 스타일 */
.dp-full-width {
  width: 100%;
}

.dp-full-width :deep(.dp__input) {
  height: 40px;
  border-radius: 4px;
  border-color: #C7C7C7;
}

/* 모달 액션 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-actions) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}
</style>